"use client";

import React, { useState, useEffect } from "react";
import { FormBlockProps, FormField, SelectField } from "./types";
import {
  InputText,
  InputTextArea,
  InputSelect,
  Button,
  DropdownSection,
  DropdownOption,
  Checkbox,
} from "@ama-pt/agora-design-system";
import Heading from "@/components/molecules/Heading";
import Image from "@/components/atoms/image";
import { convertLexicalWithInternalLinks } from "@/lib/lexical";
import { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import { useTranslations } from "next-intl";

interface FormValues {
  [key: string]: any;
}

export default function FormBlock(data: FormBlockProps) {
  const t = useTranslations();
  const [formValues, setFormValues] = useState<FormValues>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  useEffect(() => {
    const initialValues: FormValues = {};

    data.form.fields.forEach((field) => {
      if (field.type !== "message") {
        const fieldWithDefault = field as any;
        if (field.type === "checkbox") {
          initialValues[field.name] = fieldWithDefault.defaultValue || false;
        } else {
          initialValues[field.name] = fieldWithDefault.defaultValue || "";
        }
      }
    });

    setFormValues(initialValues);
  }, [data.form.fields]);

  const sanitizeFieldId = (fieldName: string) =>
    `form-field-${fieldName}`
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();

  const isPhoneField = (field: { name?: string; label?: string; type?: string }) => {
    const haystack = `${field.name || ""} ${field.label || ""}`
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    return (
      haystack.includes("telefone") ||
      haystack.includes("telemovel") ||
      haystack.includes("phone")
    );
  };

  const sanitizePhoneValue = (value: string) => value.replace(/\D/g, "");

  useEffect(() => {
    const requiredFields = data.form.fields.filter(
      (field) => field.type !== "message" && field.required
    );

    const valid = requiredFields.every((field) => {
      if (!("name" in field)) return true;
      const value = formValues[field.name];

      if (!value || (typeof value === "string" && !value.trim())) {
        return false;
      }

      if (field.type === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);
      }

      if (isPhoneField(field)) {
        return sanitizePhoneValue(value).length >= 9;
      }

      return true;
    });

    setIsValid(valid);
  }, [formValues, data.form.fields]);

  const handleFieldChange = (name: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (value && String(value).trim()) {
      let error = "";
      
      const field: any = data.form.fields.find(f => f.type !== "message" && "name" in f && f.name === name);
      
      if (field) {
        if (field.type === "email") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            error = "Email inválido";
          }
        }
        
        if (isPhoneField(field)) {
          const numbersOnly = sanitizePhoneValue(String(value));
          if (numbersOnly.length > 0 && numbersOnly.length < 9) {
            error = "Telefone deve ter no mínimo 9 dígitos";
          }
        }
      }

      setFormErrors((prev) => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[name] = error;
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });
    } else {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateField = (field: any, value: any): string => {
    if (!value || (typeof value === "string" && !value.trim())) {
      if (field.required) {
        return "Campo obrigatório";
      }
      return "";
    }

    if (field.type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "Email inválido";
      }
    }

    if (isPhoneField(field)) {
      if (sanitizePhoneValue(String(value)).length < 9) {
        return "Telefone deve ter no mínimo 9 dígitos";
      }
    }

    return "";
  };

  const submitToPayload = async (formData: FormValues) => {
    const dataToSend = Object.entries(formData).map(([name, value]) => ({
      field: name,
      value: value.toString(),
    }));

    const response = await fetch("/api/form-submissions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        form: 1,
        submissionData: dataToSend,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Erro response:", errorData);
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    return response.json();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors: Record<string, string> = {};

    data.form.fields.forEach((field) => {
      if (field.type !== "message" && "name" in field) {
        const value = formValues[field.name];
        const error = validateField(field, value);
        if (error) {
          errors[field.name] = error;
        }
      }
    });

    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      await submitToPayload(formValues);

      setSubmitStatus("success");

      const initialValues: FormValues = {};
      data.form.fields.forEach((field) => {
        if (field.type !== "message") {
          if (field.type === "checkbox") {
            initialValues[field.name] = false;
          } else {
            initialValues[field.name] = "";
          }
        }
      });
      setFormValues(initialValues);
      setFormErrors({});

      if (data.form.redirect) {
        setTimeout(() => {
          if (
            data?.form?.redirect &&
            data?.form?.redirect.type === "reference" &&
            data.form.redirect.reference?.value.slug
          ) {
            window.location.href = `/${data.form.redirect.reference.value.slug}`;
          } else if (data?.form?.redirect && data.form.redirect.url) {
            window.location.href = data.form.redirect.url;
          }
        }, 2000);
      }
    } catch (error) {
      console.error("Erro ao enviar formulário:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldErrorProps = (fieldName: string) => {
    const error = formErrors[fieldName];
    return {
      id: sanitizeFieldId(fieldName),
      hasError: !!error,
      hasFeedback: !!error,
      feedbackText: error || undefined,
    };
  };

  const renderField = (field: FormField, index: number) => {
    const getGridColSpan = (width?: number) => {
      if (!width) return "col-span-12";

      const colSpan = Math.round((width / 100) * 12);
      return `col-span-12 md:col-span-${Math.max(1, Math.min(12, colSpan))}`;
    };

    const containerClass =
      field.type !== "message" ? getGridColSpan(field.width) : "col-span-12";

    switch (field.type) {
      case "text":
        return (
          <div key={field.name} className={containerClass}>
            <InputText
              label={
                (
                  <>
                    {field.required && (
                      <span className="text-danger-500 text-[24px]">*</span>
                    )}
                    {field.label}
                  </>
                ) as any
              }
              required={field.required}
              value={formValues[field.name] || ""}
              type={isPhoneField(field) ? "tel" : "text"}
              inputMode={isPhoneField(field) ? "tel" : undefined}
              onChange={(e) => {
                const value = isPhoneField(field)
                  ? sanitizePhoneValue(e.target.value)
                  : e.target.value;
                handleFieldChange(field.name, value);
              }}
              placeholder={`${field.placeholder}`}
              {...getFieldErrorProps(field.name)}
            />
          </div>
        );

      case "email":
        return (
          <div key={field.name} className={containerClass}>
            <InputText
              label={
                (
                  <>
                    {field.required && (
                      <span className="text-danger-500 text-[24px]">*</span>
                    )}
                    {field.label}
                  </>
                ) as any
              }
              required={field.required}
              value={formValues[field.name] || ""}
              type="email"
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={`${field.placeholder}`}
              {...getFieldErrorProps(field.name)}
            />
          </div>
        );

      case "number":
        return (
          <div key={field.name} className={containerClass}>
            <InputText
              label={
                (
                  <>
                    {field.required && (
                      <span className="text-danger-500 text-[24px]">*</span>
                    )}
                    {field.label}
                  </>
                ) as any
              }
              required={field.required}
              value={formValues[field.name] || ""}
              type={isPhoneField(field) ? "tel" : "number"}
              onChange={(e) => {
                const value = isPhoneField(field)
                  ? sanitizePhoneValue(e.target.value)
                  : e.target.value;
                handleFieldChange(field.name, value);
              }}
              placeholder={`${field.placeholder}`}
              inputMode={isPhoneField(field) ? "tel" : "numeric"}
              pattern={isPhoneField(field) ? "[0-9]*" : undefined}
              {...getFieldErrorProps(field.name)}
            />
          </div>
        );

      case "textarea":
        return (
          <div key={field.name} className={containerClass}>
            <InputTextArea
              label={
                (
                  <>
                    {field.required && (
                      <span className="text-danger-500 text-[24px]">*</span>
                    )}
                    {field.label}
                  </>
                ) as any
              }
              required={field.required}
              value={formValues[field.name] || ""}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={`${field.placeholder}`}
              {...getFieldErrorProps(field.name)}
            />
          </div>
        );

      case "select":
        const selectField = field as SelectField;
        return (
          <div key={field.name} className={containerClass}>
            <InputSelect
              label={
                (
                  <>
                    {field.required && (
                      <span className="text-danger-500 text-[24px]">*</span>
                    )}
                    {field.label}
                  </>
                ) as any
              }
              placeholder={selectField.placeholder || `${field.placeholder}`}
              dropdownAriaLabel={`Lista de ${field.label.toLowerCase()}`}
              onChange={(selected) => {
                const value = selected.length > 0 ? selected[0].value : "";
                handleFieldChange(field.name, value);
              }}
              {...getFieldErrorProps(field.name)}
            >
              <DropdownSection
              className={`${
                selectField.options.length > 4
                  ? "h-[300px]"
                  : `h-[${selectField.options.length * 1}px]`
              }`}
              name={field.name}>
                {selectField.options.map((option, optionIndex) => (
                  <DropdownOption
                    key={`${field.name}-${optionIndex}`}
                    value={option.value}
                    selected={formValues[field.name] === option.value}
                  >
                    {option.label}
                  </DropdownOption>
                ))}
              </DropdownSection>
            </InputSelect>
          </div>
        );

      case "checkbox":
        return (
          <div key={field.name} className={containerClass}>
            <Checkbox
              label={
                (
                  <>
                    {field.required && (
                      <span className="text-danger-500 text-[24px]">*</span>
                    )}
                    {field.label}
                  </>
                ) as any
              }
              checked={Boolean(formValues[field.name])}
              onChange={(checked) => handleFieldChange(field.name, checked)}
              {...getFieldErrorProps(field.name)}
            />
          </div>
        );

      case "country":
      case "state":
        return (
          <div key={field.name} className={containerClass}>
            <InputText
              label={
                (
                  <>
                    {field.required && (
                      <span className="text-danger-500 text-[24px]">*</span>
                    )}
                    {field.label}
                  </>
                ) as any
              }
              required={field.required}
              value={formValues[field.name] || ""}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={`${field.placeholder}`}
              {...getFieldErrorProps(field.name)}
            />
          </div>
        );

      case "message":
        return (
          <div
            key={`message-${index}`}
            className="col-span-12 p-4 bg-blue-50 border border-blue-200 rounded-md"
          >
            <p className="text-blue-800">{field.message}</p>
          </div>
        );

      default:
        return null;
    }
  };

  const html = convertLexicalWithInternalLinks(
    data.description as SerializedEditorState
  );
  const sucessMesage = convertLexicalWithInternalLinks(
    data.form.confirmationMessage as unknown as SerializedEditorState
  );
  const hasRequiredFields = data.form.fields.some(
    (field) => field.type !== "message" && field.required
  );

  return (
    <div className="container mx-auto py-16">
      <div className="">
        <div className="grid grid-cols-1 items-start xl:grid-cols-2 gap-32">
          <div className="order-2 xl:order-1">
            <Heading title={data.title} />
            <div className="mb-32" dangerouslySetInnerHTML={{ __html: html }} />

            <form onSubmit={handleSubmit}>
              {hasRequiredFields && (
                <p className="mb-32 text-danger-500">
                  <span className="text-[24px]" aria-hidden="true">*</span>
                  <span className="text-s-regular"> {t("form.requiredFieldNote")}</span>
                </p>
              )}

              <div className="grid grid-cols-12 gap-16">
                {data.form.fields.map((field, index) =>
                  renderField(field, index)
                )}
              </div>

              <div className="mt-32">
                <Button
                  type="submit"
                  disabled={isSubmitting || !isValid}
                  className={`${
                    !isValid ? "opacity-50 cursor-not-allowed" : ""
                  } w-full md:w-auto`}
                  hasIcon
                  appearance="solid"
                  trailingIcon="agora-line-arrow-right-circle"
                >
                  {isSubmitting
                    ? t("form.submitting")
                    : data.form.submitButtonLabel}
                </Button>

                {submitStatus === "success" &&
                  data.form.confirmationMessage && (
                    <div className="mt-16">
                      <div
                        className="text-primary-500"
                        dangerouslySetInnerHTML={{ __html: sucessMesage }}
                      />
                    </div>
                  )}

                {submitStatus === "error" && (
                  <div className="mt-6 p-4 bg-danger-50 border border-danger-200 rounded-md">
                    <p className="text-danger-600">
                      Ocorreu um erro ao enviar o formulário. Tente novamente.
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
          {data.image && (
            <div className="flex justify-center xl:justify-end mt-0 xl:order-2 xl:mt-256">
              <Image
                src={data.image.url}
                alt={data.image.alt}
                width={488}
                height={406}
                className="w-full max-w-[488px] max-h-[406px] h-auto rounded-lg object-contain"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
